export const GENESIS_VIEW_ORIGIN = 'https://view.genesisframeworks.com';
export const GENESIS_BOX_ORIGIN = 'https://box.genesisframeworks.com';
export const GENESIS_BOX_FIREBASE_ORIGIN = 'https://genesisbox-2261b.web.app';

export const VIEW_CORS_ALLOWED_ORIGINS = [
  GENESIS_VIEW_ORIGIN,
  GENESIS_BOX_ORIGIN,
  GENESIS_BOX_FIREBASE_ORIGIN,
] as const;

export const VIEW_CORS_ALLOWED_METHODS = ['GET', 'HEAD', 'OPTIONS'] as const;
export const VIEW_CORS_R2_ALLOWED_METHODS = ['GET', 'HEAD', 'PUT'] as const;
export const VIEW_CORS_ALLOWED_HEADERS = [
  'Range',
  'Content-Type',
  'If-Modified-Since',
  'If-None-Match',
] as const;
export const VIEW_CORS_EXPOSED_HEADERS = [
  'Accept-Ranges',
  'Content-Length',
  'Content-Range',
  'Content-Encoding',
  'Content-Type',
  'ETag',
  'Last-Modified',
  'cf-cache-status',
  'cf-share-version',
] as const;

export const VIEW_CORS_PDF_CORE_EXPOSED_HEADERS = [
  'Accept-Ranges',
  'Content-Length',
  'Content-Range',
] as const;

export const VIEW_CORS_MAX_AGE_SECONDS = 86400;

type HeaderReader = Pick<Headers, 'get'> | Record<string, string | null | undefined>;

function formatHeaderList(values: readonly string[]): string {
  return values.join(', ');
}

function readHeader(headers: HeaderReader, name: string): string | null {
  if ('get' in headers && typeof headers.get === 'function') {
    return headers.get(name);
  }
  const headerRecord = headers as Record<string, string | null | undefined>;
  const direct = headerRecord[name];
  if (direct) return direct;
  const lower = headerRecord[name.toLowerCase()];
  if (lower) return lower;
  const foundKey = Object.keys(headerRecord).find((key) => key.toLowerCase() === name.toLowerCase());
  return foundKey ? headerRecord[foundKey] || null : null;
}

function shellDoubleQuote(value: string): string {
  return `"${value.replace(/(["\\$`])/g, '\\$1')}"`;
}

export function buildCloudflareR2CorsJson(
  allowedOrigins: readonly string[] = VIEW_CORS_ALLOWED_ORIGINS,
): string {
  return JSON.stringify(
    [
      {
        AllowedOrigins: allowedOrigins,
        AllowedMethods: VIEW_CORS_R2_ALLOWED_METHODS,
        AllowedHeaders: VIEW_CORS_ALLOWED_HEADERS,
        ExposeHeaders: VIEW_CORS_EXPOSED_HEADERS,
        MaxAgeSeconds: VIEW_CORS_MAX_AGE_SECONDS,
      },
    ],
    null,
    2,
  );
}

export function buildDreamHostHtaccessCorsSnippet(
  allowedOrigins: readonly string[] = VIEW_CORS_ALLOWED_ORIGINS,
): string {
  const originPattern = allowedOrigins
    .map((origin) => origin.replace(/^https:\/\//, '').replace(/\./g, '\\.'))
    .join('|');

  return [
    '<IfModule mod_headers.c>',
    `  SetEnvIfNoCase Origin "^https://(${originPattern})$" VIEW_CORS_ORIGIN=$0`,
    '  Header always set Access-Control-Allow-Origin "%{VIEW_CORS_ORIGIN}e" env=VIEW_CORS_ORIGIN',
    '  Header always merge Vary Origin',
    `  Header always set Access-Control-Allow-Methods "${formatHeaderList(VIEW_CORS_ALLOWED_METHODS)}"`,
    `  Header always set Access-Control-Allow-Headers "${formatHeaderList(VIEW_CORS_ALLOWED_HEADERS)}"`,
    `  Header always set Access-Control-Expose-Headers "${formatHeaderList(VIEW_CORS_EXPOSED_HEADERS)}"`,
    `  Header always set Access-Control-Max-Age "${VIEW_CORS_MAX_AGE_SECONDS}"`,
    '</IfModule>',
  ].join('\n');
}

export function buildPublicFilesHtaccessCorsSnippet(): string {
  return [
    '<IfModule mod_headers.c>',
    '  Header always set Access-Control-Allow-Origin "*"',
    '  Header always set Access-Control-Allow-Methods "GET, HEAD, OPTIONS"',
    `  Header always set Access-Control-Allow-Headers "${formatHeaderList(VIEW_CORS_ALLOWED_HEADERS)}"`,
    `  Header always set Access-Control-Expose-Headers "${formatHeaderList(VIEW_CORS_EXPOSED_HEADERS)}"`,
    `  Header always set Access-Control-Max-Age "${VIEW_CORS_MAX_AGE_SECONDS}"`,
    '</IfModule>',
  ].join('\n');
}

export function buildViewCorsCurlCommand(
  fileUrl: string,
  origin = GENESIS_VIEW_ORIGIN,
): string {
  const target = fileUrl.trim() || 'https://your-file-domain.example/path/file.pdf';
  return [
    'curl -sS -D - -o /dev/null \\',
    `  -H ${shellDoubleQuote(`Origin: ${origin}`)} \\`,
    `  -H ${shellDoubleQuote('Range: bytes=0-0')} \\`,
    `  ${shellDoubleQuote(target)}`,
  ].join('\n');
}

export function getVisibleViewCorsHeaders(headers: HeaderReader): string[] {
  return VIEW_CORS_EXPOSED_HEADERS.filter((header) => Boolean(readHeader(headers, header)));
}

export function getMissingViewCorsCoreHeaders(headers: HeaderReader): string[] {
  return VIEW_CORS_PDF_CORE_EXPOSED_HEADERS.filter((header) => !readHeader(headers, header));
}

export function hasReadablePdfRangeHeaders(headers: HeaderReader): boolean {
  return Boolean(readHeader(headers, 'Content-Range')) || /bytes/i.test(readHeader(headers, 'Accept-Ranges') || '');
}
